import { PanelsState } from '../types/panels-state.js';
import {
  Panels,
  PanelSizesWithUnit,
  PanelChildren,
} from '../types/shared-panel-types.js';

/**
 * Get a sorted list of all the registered child panels.
 */
export function getSortedPanelChildren(panels: Panels): PanelChildren {
  const panelChildren: PanelChildren = [];

  for (const [panelName, panel] of Object.entries(panels)) {
    panelChildren.push({ ...panel, name: panelName });
  }

  panelChildren.sort((a, b) => {
    return a.order - b.order;
  });

  return panelChildren;
}

/** Search for the panel with given name in a panels array. */
export function getPanelByName(panels: PanelChildren, name: string) {
  return panels.find((panel) => panel.name === name);
}

/**
 * Get the index of a named panel in the array of sorted panels.
 * @param sortedPanels  - Array of panels sorted by order.
 * @returns Panel index or -1.
 */
export function getPanelIndex(
  panelName: string,
  sortedPanels: PanelChildren,
): number {
  return sortedPanels.findIndex((panel) => panel.name === panelName);
}

/**
 * Get the index of the next panel.
 * @param sortedPanels - Array of panels sorted by order.
 * @returns Panel index or -1.
 */
export function getNextPanelIndex(
  sortedPanels: PanelChildren,
  currentIndex: number,
  options?: {
    direction?: 1 | -1;
    loop?: boolean;
    skipNonResizable?: boolean;
  },
): number {
  const { direction = 1, loop = false } = options || {};
  let nextIndex = currentIndex + 1 * direction;

  if (nextIndex < 0 || nextIndex > sortedPanels.length - 1) {
    if (!loop) {
      // We are not looping, but the next index is already out of bounds.
      return -1;
    }

    nextIndex = nextIndex < 0 ? sortedPanels.length - 1 : 0;
  }

  if (options?.skipNonResizable) {
    while (!sortedPanels[nextIndex].resizable) {
      nextIndex += 1 * direction;

      if (nextIndex < 0 || nextIndex > sortedPanels.length - 1) {
        if (loop) {
          nextIndex = nextIndex < 0 ? sortedPanels.length - 1 : 0;
        } else {
          return -1;
        }
      }
    }
  }

  return nextIndex;
}

export function getRemovedPanels(
  prevPanels: PanelChildren,
  state: PanelsState,
) {
  return prevPanels.reduce((removedPanels: string[], prevPanel) => {
    if (!state.panels[prevPanel.name]) {
      removedPanels.push(prevPanel.name);
    }

    return removedPanels;
  }, []);
}

export function getAddedPanels(
  panelKeys: string[],
  sizes: PanelSizesWithUnit,
  name: string,
) {
  return panelKeys.reduce((acc: string[], panelName) => {
    if (!Object.keys(sizes).includes(panelName) && panelName !== name) {
      acc.push(panelName);
    }
    return acc;
  }, []);
}
